import React, { useContext, useEffect, useState } from 'react'
import { Button, Col, Container, Inline, Modal, Row, Typography } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/pageHeader'
import useMutation from 'utils/hooks/useMutation'
import URL_APIS from 'utils/api/urlApis'
import { Lang } from 'context/LangContext'
import { LinkedDocInput } from 'types/workorder/linkedDoc'
import useOptions from 'utils/hooks/useOptions'
import { FormProvider, useForm } from 'react-hook-form'
import FormSelect from 'components/FormFields/FormSelect'
import FormInput from 'components/FormFields/FormInput'
import ModalConfirm from 'components/Modal/ModalConfim'
import LightDarkContext from 'context/LightDarkModeContext'

// List mapping fields in error after a submission returning errors/warnings
const messagesErrWarnFromBack = {}

type Props = {
  lang: Lang
  open: boolean
  onClose: () => void
  linkedDocument?: LinkedDocInput
  onPostSubmit: () => void
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '70%',
  width: '60%',
}

const ModalLinkedDoc = ({ lang, open, onClose, linkedDocument, onPostSubmit }: Props) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { mutate: createLinkedDoc } = useMutation<LinkedDocInput>(
    URL_APIS.URL_WORKORDER_LINKED_DOCS_POST,
    '',
    messagesErrWarnFromBack,
  )

  const { loadOptions: loadOptionsLinkedDocTypes, options: linkedDocTypeOptions } = useOptions(
    URL_APIS.URL_COMMON_LIST_LINKEDDOCTYPES + '?lang=fr',
  )
  const { loadOptions: loadOptionsDocTypes, options: docTypeOptions } = useOptions(URL_APIS.URL_COMMON_LIST_DOCTYPES)
  const { loadOptions: loadOptionsProtocols, options: protocolOptions } = useOptions(URL_APIS.URL_COMMON_LIST_PROTOCOLS)

  const methods = useForm<LinkedDocInput>({
    values: linkedDocument,
  })

  const {
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    resetField,
    watch,
  } = methods

  const selectedLinkedDocType = watch('codeType')

  const onSubmit = async (data: LinkedDocInput) => {
    setIsLoading(true)

    try {
      await createLinkedDoc({
        data: {},
        params: {
          lang: lang,
          oenumdoc: data.oenumDoc,
          linkType: data.linkType,
          codeType: data.codeType,
          protocol: data.protocol,
          typeDocSb: data.typeDocSb,
          reference: data.reference.toUpperCase(),
          revision: data.revision.toUpperCase(),
          item: data.item.toUpperCase(),
        },
        method: 'POST',
      })
      reset()
      onPostSubmit()
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setConfirmOpen(false)
    reset()
    onClose()
  }

  const clearFields = () => {
    resetField('typeDocSb')
    resetField('protocol')
    resetField('reference')
    resetField('revision')
    resetField('item')
  }

  useEffect(() => {
    loadOptionsLinkedDocTypes({}, 'code', 'label', 'label', true)
    loadOptionsDocTypes()
    loadOptionsProtocols()
  }, [])
  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            lang={lang}
            pageTitle={t('1M00128')}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <div style={{ width: '100%' }}>
                    <FormSelect
                      name='codeType'
                      label={t('9510098')}
                      rules={{ required: true }}
                      options={linkedDocTypeOptions}
                      error={Boolean(errors?.codeType)}
                      onChange={clearFields}
                    />
                  </div>

                  <div style={{ margin: '20px 50px' }}>
                    <Row justify='space-between'>
                      {['-', '6'].includes(selectedLinkedDocType) && (
                        <Col xxs={5}>
                          <FormSelect
                            name='typeDocSb'
                            label={t('1C00018')}
                            rules={{ required: true }}
                            options={docTypeOptions}
                            error={Boolean(errors?.typeDocSb)}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row justify='space-between'>
                      {['-', '2', '3'].includes(selectedLinkedDocType) && (
                        <Col xxs={5}>
                          <FormSelect
                            name='protocol'
                            label={t('1P00005')}
                            rules={{ required: selectedLinkedDocType !== '-' }}
                            options={protocolOptions}
                            error={Boolean(errors?.protocol)}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row justify='space-between'>
                      {selectedLinkedDocType && (
                        <Col xxs={5}>
                          <FormInput
                            name='reference'
                            label={t('9500042')}
                            maxLength={40}
                            capitalize
                            error={Boolean(errors.reference)}
                            rules={{
                              required: true,
                              validate: (value) => !/\s/.test(value),
                            }}
                            fullWidth
                          />
                          {errors.reference && (
                            <Typography variant='xsmall'>Les espaces ne sont pas autorisés </Typography>
                          )}
                        </Col>
                      )}
                      {['-', '6'].includes(selectedLinkedDocType) && (
                        <Col xxs={5}>
                          <FormInput
                            name='revision'
                            label={t('1LEX050')}
                            maxLength={4}
                            capitalize
                            error={Boolean(errors.revision)}
                            fullWidth
                          />
                        </Col>
                      )}
                    </Row>
                    <Row justify='space-between'>
                      {['-', '6'].includes(selectedLinkedDocType) && (
                        <Col xxs={5}>
                          <FormInput
                            name='item'
                            label={t('0MRM050')}
                            maxLength={2}
                            capitalize
                            error={Boolean(errors.item)}
                            fullWidth
                          />
                        </Col>
                      )}
                    </Row>
                  </div>
                </div>
                <Inline
                  align='space-around'
                  style={{
                    padding: '10px',
                    position: 'absolute',
                    bottom: 0,
                    marginTop: '1em',
                    width: '100%',
                  }}
                >
                  <Button onClick={() => setConfirmOpen(true)} variant='secondary'>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' disabled={isLoading || !isDirty}>
                    {t('9500174')}
                  </Button>
                </Inline>
              </form>
            </FormProvider>
          </Container>
        </div>
      </Modal>
      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={confirmOpen}
        onCloseModal={() => setConfirmOpen(false)}
        onSubmit={handleClose}
      />
    </>
  )
}

export default ModalLinkedDoc
